/*
 * The free-event banner (design W2, look 2A: a ticket stub on the left, two
 * actions at most). It follows the event's whole life from the user payload's
 * `free_event` (./state.js) and draws nothing for an account without one.
 * Status lines sit inside the banner (Req 21), never in a toast.
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  GiftIcon,
  CalendarDaysIcon,
  ChatBubbleLeftEllipsisIcon,
  PhotoIcon,
  ClockIcon,
  ArrowPathIcon,
  XCircleIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { t } from "copy/t";
import { ft, fw, fc, Btn, Msg } from "components/FreeEvent";
import { selectUser } from "containers/App/selectors";
import { downloadLeads } from "utils/api/leads";
import { resendVerificationEmail } from "utils/api/session";
import { formatEventDateTime, calendarDaysUntil, noWidow } from "utils/eventTime";
import { trackBannerViewed, trackBannerAction, trackLeadsCsvDownloaded } from "utils/freeEventAnalytics";
import { bannerState, BANNER } from "./state";
import "containers/FreeStart/free-start.css";

const ICONS = {
  avail: GiftIcon,
  avail0: GiftIcon,
  useby: CalendarDaysIcon,
  useby0: CalendarDaysIcon,
  warn: ChatBubbleLeftEllipsisIcon,
  cap: ChatBubbleLeftEllipsisIcon,
  ret: PhotoIcon,
  soon: ClockIcon,
  off: ArrowPathIcon,
  gone: XCircleIcon,
  ref: DevicePhoneMobileIcon,
  unused: ClockIcon,
  unv: EnvelopeIcon,
  eu: UserIcon,
};

const selectUserState = selectUser();

// Every placeholder the banner lines use, read in the event's zone (Req 4).
export const bannerVars = (fe, now = new Date()) => {
  const tz = fe.event_time_zone;
  const when = (iso) => (iso ? formatEventDateTime(iso, tz, now) : "");
  const start = fe.window_starts_at ? calendarDaysUntil(fe.window_starts_at, tz, now) : 0;
  return {
    available_until: when(fe.available_until),
    window_ends_at: when(fe.window_ends_at),
    gallery_retained_until: when(fe.gallery_retained_until),
    gallery_restorable_until: when(fe.gallery_restorable_until),
    days_left: fe.gallery_retained_until ? Math.max(0, calendarDaysUntil(fe.gallery_retained_until, tz, now)) : 0,
    day: Math.min(3, Math.max(1, 1 - start)),
    sms_sent_count: fe.sms_sent_count || 0,
    sms_cap: fe.sms_cap || 0,
  };
};

// CSV export: the existing leads export, which emails the file.
const useCsv = (token, channelId) => {
  const [csv, setCsv] = useState("idle");
  const run = async () => {
    if (csv === "busy") return;
    setCsv("busy");
    try {
      await downloadLeads(token, channelId ? `?source_channel=${channelId}` : "");
      trackLeadsCsvDownloaded();
      setCsv("ok");
    } catch (_e) {
      setCsv("err");
    }
  };
  return [csv, run];
};

export const FreeEventBannerView = ({ fe, user, now }) => {
  const navigate = useNavigate();
  const [, setParams] = useSearchParams();
  const state = bannerState(fe, now);
  const [csv, runCsv] = useCsv(user && user.authToken, fe && fe.channel_id);
  const [resend, setResend] = useState("idle");

  useEffect(() => {
    if (state) trackBannerViewed(state);
  }, [state]);

  if (!state) return null;
  const def = BANNER[state];
  const vars = bannerVars(fe, now);
  const act = (name, fn) => () => {
    trackBannerAction(state, name);
    fn();
  };
  const openPlans = act("plans", () =>
    setParams(
      (p) => {
        p.set("plans", "open");
        return p;
      },
      { replace: false }
    )
  );
  const toLeads = act("leads", () => navigate(fe.channel_id ? `/leads/${fe.channel_id}` : "/leads"));
  const plansBtn = (cls) => (
    <Btn className={`sm ${cls}`} onClick={openPlans}>
      {ft("fb.plans")}
    </Btn>
  );

  let actions = null;
  let meter = null;
  if (def.act === "avail") {
    actions = (
      <Btn className="sm" onClick={act("setup", () => navigate(`/channels/${fe.channel_id}/edit`))}>
        {ft("fb.av.a")}
      </Btn>
    );
  } else if (def.act === "live") {
    meter = (
      <div className="meter">
        <div className="days" aria-hidden="true">
          {[1, 2, 3].map((d) => (
            <i key={d} className={d <= vars.day ? "on" : ""} />
          ))}
        </div>
        {ft("fb.live.m", vars)}
      </div>
    );
    actions = (
      <Btn className="sm ghost" onClick={toLeads}>
        {ft("fb.leads")}
      </Btn>
    );
  } else if (def.act === "meter") {
    const pct = vars.sms_cap ? Math.min(100, Math.round((vars.sms_sent_count / vars.sms_cap) * 100)) : 100;
    meter = (
      <div className="meter">
        <div className="bar" role="presentation">
          <i style={{ width: `${pct}%` }} />
        </div>
        {`${vars.sms_sent_count} / ${vars.sms_cap}`}
      </div>
    );
    actions = (
      <Btn className="sm ghost" onClick={toLeads}>
        {ft("fb.leads")}
      </Btn>
    );
  } else if (def.act === "ret") {
    actions = (
      <>
        <Btn className="sm" busy={csv === "busy"} busyLabel={ft("li.busy")} onClick={act("csv", runCsv)}>
          {ft(csv === "err" ? "fb.retry" : "fb.csv")}
        </Btn>
        {plansBtn("ghost")}
      </>
    );
  } else if (def.act === "unv") {
    const send = act("resend", async () => {
      setResend("busy");
      try {
        await resendVerificationEmail({ email: user.email });
        setResend("ok");
      } catch (_e) {
        setResend("err");
      }
    });
    actions = (
      <Btn className="sm" busy={resend === "busy"} onClick={send}>
        {ft("fb.unv.a")}
      </Btn>
    );
  } else if (def.act === "plans") {
    actions = plansBtn("");
  }

  const Icon = ICONS[state];
  const body = state === "soon" ? noWidow(fc("fb.soon", vars.days_left, vars)) : fw(def.p, vars);

  return (
    <section className={`mb-fs fb ${def.cls}`} role="status" aria-label={ft("fb.label")}>
      <div className="stub">
        {state === "live" ? <span className="live-dot" /> : <Icon className="ic" aria-hidden="true" />}
        {ft(def.stub)}
      </div>
      <div className="bd">
        <div className="tx">
          <b>{fw(def.b, vars)}</b>
          <p>{body}</p>
          {meter}
        </div>
        {actions && <div className="act">{actions}</div>}
        {csv === "err" && (
          <Msg className="ierr" id="fb-csv-err">
            {ft("fb.csv.err")}
          </Msg>
        )}
        {csv === "ok" && (
          <Msg kind="ok" className="ierr">
            {t("toast.leads.csvExportStarted")}
          </Msg>
        )}
        {resend === "ok" && (
          <Msg kind="ok" className="ierr">
            {t("toast.signIn.verificationSent")}
          </Msg>
        )}
        {resend === "err" && <Msg className="ierr">{t("toast.signIn.verificationFailed")}</Msg>}
      </div>
    </section>
  );
};

const FreeEventBanner = () => {
  const user = useSelector(selectUserState);
  const fe = user && user.free_event;
  if (!fe) return null;
  return <FreeEventBannerView fe={fe} user={user} />;
};

export default FreeEventBanner;
